import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { emitStaffChanged } from '../../lib/events';
import { DESIGNATION_BADGES, ROLE_BADGES, DESIGNATIONS } from '../../lib/format';
const NONE = '__none__';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { InfoRow, ActivityList } from './SummaryShared';
import { Pencil, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const staffFormFrom = (m) => ({
  name: m.name || '', email: m.email || '', phone: m.phone || '',
  employee_code: m.employee_code || '', designation: m.role || 'Sales Executive',
  team: (m.teams || [])[0] || '', reporting_manager_id: m.reporting_manager_id || NONE,
  status: m.status || 'Active', is_setter: !!m.is_setter, is_closer: !!m.is_closer,
});

const staffPayload = (f) => ({
  name: f.name.trim(), email: f.email.trim() || null, phone: f.phone || null,
  employee_code: f.employee_code || null, designation: f.designation,
  team: f.team.trim() || null,
  reporting_manager_id: f.reporting_manager_id === NONE ? null : f.reporting_manager_id,
  status: f.status, is_setter: f.is_setter, is_closer: f.is_closer,
});

const validateStaff = (f) => {
  if (!f.name.trim()) return 'Name is required';
  if (f.email.trim() && !EMAIL_RX.test(f.email.trim())) return 'Invalid email address format';
  return null;
};

const StaffField = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const RolesRow = ({ f, set, hasLogin }) => (
  <div className="flex items-center justify-between rounded-lg border p-2.5">
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-1.5 text-xs"><Switch checked={f.is_setter} onCheckedChange={(v) => set('is_setter', v)} data-testid="staff-panel-setter-switch" /> Setter</label>
      <label className="flex items-center gap-1.5 text-xs"><Switch checked={f.is_closer} onCheckedChange={(v) => set('is_closer', v)} data-testid="staff-panel-closer-switch" /> Closer</label>
    </div>
    {hasLogin ? (
      <span className="text-[10px] text-muted-foreground">Status via Directory (has login)</span>
    ) : (
      <label className="flex items-center gap-1.5 text-xs">
        <Switch checked={f.status === 'Active'} onCheckedChange={(v) => set('status', v ? 'Active' : 'Inactive')} data-testid="staff-panel-status-switch" /> Active
      </label>
    )}
  </div>
);

/** Directly editable staff panel \u2014 saves via the existing RBAC-guarded PUT /staff API
 * and broadcasts staff:changed so directories, filters and dropdowns refresh instantly. */
export const StaffEditForm = ({ data, options, onSaved }) => {
  const m = data.member;
  const [f, setF] = useState(() => staffFormFrom(m));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(staffFormFrom(m)); }, [m]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const err = validateStaff(f);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = staffPayload(f);
      const { data: updated } = await api.put(`/staff/${m.id}`, payload);
      toast.success(`${payload.name} updated \u2014 synced everywhere`);
      emitStaffChanged(updated);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save staff profile');
    } finally { setSaving(false); }
  };

  const managers = (options.managers || []).filter((r) => r.member_id && r.member_id !== m.id && r.status === 'Active');

  return (
    <div className="space-y-3" data-testid="staff-inline-edit-form">
      <div className="grid grid-cols-2 gap-2">
        <StaffField label="Staff Name *"><Input value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="staff-panel-name-input" /></StaffField>
        <StaffField label="Employee Code"><Input value={f.employee_code} onChange={(e) => set('employee_code', e.target.value)} data-testid="staff-panel-code-input" /></StaffField>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StaffField label="Email"><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="staff-panel-email-input" /></StaffField>
        <StaffField label="Phone"><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} data-testid="staff-panel-phone-input" /></StaffField>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StaffField label="Role / Designation">
          <Select value={f.designation} onValueChange={(v) => set('designation', v)}>
            <SelectTrigger data-testid="staff-panel-designation-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </StaffField>
        <StaffField label="Team">
          <Input value={f.team} onChange={(e) => set('team', e.target.value)} placeholder="e.g. Alpha" data-testid="staff-panel-team-input" />
        </StaffField>
      </div>
      <StaffField label="Reporting Manager">
        <Select value={f.reporting_manager_id} onValueChange={(v) => set('reporting_manager_id', v)}>
          <SelectTrigger data-testid="staff-panel-manager-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No manager</SelectItem>
            {managers.map((r) => <SelectItem key={r.member_id} value={r.member_id}>{r.name} — {r.designation}</SelectItem>)}
          </SelectContent>
        </Select>
      </StaffField>
      <RolesRow f={f} set={set} hasLogin={!!data.user} />
      <Button className="w-full" onClick={save} disabled={saving} data-testid="staff-panel-save-button">
        {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Pencil size={14} className="mr-1.5" />}
        Save Staff Profile
      </Button>
    </div>
  );
};

export const StaffSummary = ({ data, canManage, options, onSaved, onOpenDirectory }) => {
  const m = data.member;
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading font-bold text-lg leading-tight" data-testid="summary-staff-name">{m.name}</div>
          <div className="text-xs text-muted-foreground">{m.email || 'no email'}</div>
        </div>
        <Badge variant="outline" className={`shrink-0 ${m.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`} data-testid="summary-staff-status">{m.status}</Badge>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className={`text-[10px] ${DESIGNATION_BADGES[m.role] || ''}`}>{m.role}</Badge>
        {data.user?.access_role && <Badge variant="outline" className={`text-[10px] ${ROLE_BADGES[data.user.access_role] || ''}`}>{data.user.access_role}</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border p-2.5 text-center">
          <div className="font-heading text-lg font-bold" data-testid="summary-staff-open-leads">{data.open_leads}</div>
          <div className="text-[10px] text-muted-foreground uppercase">Open Leads</div>
        </div>
        <div className="rounded-lg border p-2.5 text-center">
          <div className="font-heading text-lg font-bold">{data.won_leads}</div>
          <div className="text-[10px] text-muted-foreground uppercase">Won Deals</div>
        </div>
      </div>
      <Separator />
      {canManage ? (
        <StaffEditForm data={data} options={options} onSaved={onSaved} />
      ) : (
        <div>
          <InfoRow label="Phone" value={m.phone} />
          <InfoRow label="Employee Code" value={m.employee_code} />
          <InfoRow label="Teams" value={(m.teams || []).join(', ')} />
          <InfoRow label="Reports To" value={data.reporting_manager_name} />
          <InfoRow label="Login" value={data.user ? data.user.email : 'No CRM login'} />
        </div>
      )}
      <Separator />
      <ActivityList items={data.recent_activity || []} />
      {canManage && (
        <Button variant="outline" className="w-full" onClick={onOpenDirectory} data-testid="summary-open-directory-button">
          <Users size={14} className="mr-1.5" /> Full Directory (login & access)
        </Button>
      )}
    </div>
  );
};
